import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import { searchFoods, getCategories, calculateNutrition } from '../../../data/foodDatabase';
import BarcodeScannerModal from './BarcodeScannerModal';

const CALORIE_PRESETS = [
  { label: 'Todos', min: 0, max: Infinity },
  { label: '< 50 cal', min: 0, max: 50 },
  { label: '50–150 cal', min: 50, max: 150 },
  { label: '150–300 cal', min: 150, max: 300 },
  { label: '> 300 cal', min: 300, max: Infinity },
];

const MACRO_FILTERS = [
  { key: 'protein', label: 'Proteína', color: 'text-blue-500', icon: 'Dumbbell' },
  { key: 'carbs', label: 'Carbos', color: 'text-amber-500', icon: 'Wheat' },
  { key: 'fat', label: 'Grasa', color: 'text-rose-500', icon: 'Droplets' },
];

// Common serving size presets
const SERVING_PRESETS = [
  { label: '½ porción', multiplier: 0.5, icon: 'Minus' },
  { label: '1 porción', multiplier: 1, icon: 'Circle' },
  { label: '1½ porciones', multiplier: 1.5, icon: 'Plus' },
  { label: '2 porciones', multiplier: 2, icon: 'ChevronsUp' },
];

const QUICK_GRAM_OPTIONS = [25, 50, 75, 100, 150, 200];

const MacroBar = ({ label, value, total, color, bgColor, textColor }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className={`text-xs font-medium w-16 shrink-0 ${textColor}`}>{label}</span>
      <div className="flex-1 h-2 bg-muted/40 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${bgColor}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground w-12 text-right shrink-0">
        {value}g <span className={`font-semibold ${textColor}`}>{pct}%</span>
      </span>
    </div>
  );
};

const AddFoodModal = ({ isOpen, onClose, onAddFood, mealType }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  const [selectedFood, setSelectedFood] = useState(null);
  const [grams, setGrams] = useState('');
  const [showRecent, setShowRecent] = useState(true);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [scannedProduct, setScannedProduct] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [activePreset, setActivePreset] = useState(1); // index into SERVING_PRESETS (default: 1 porción)

  // Filter state
  const [caloriePreset, setCaloriePreset] = useState(0);
  const [macroMin, setMacroMin] = useState({ protein: '', carbs: '', fat: '' });

  const categories = useMemo(() => getCategories(), []);

  // Debounce inputValue → searchQuery by 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(inputValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Single searchFoods call — derive both base and filtered from this
  const baseFilteredFoods = useMemo(
    () => searchFoods(searchQuery, selectedCategory),
    [searchQuery, selectedCategory]
  );

  const filteredFoods = useMemo(() => {
    const { min: calMin, max: calMax } = CALORIE_PRESETS?.[caloriePreset];
    return baseFilteredFoods?.filter((food) => {
      const cal = food?.calories ?? 0;
      if (cal < calMin || cal > calMax) return false;
      for (const { key } of MACRO_FILTERS) {
        const minVal = parseFloat(macroMin?.[key]);
        if (!isNaN(minVal) && minVal > 0 && (food?.[key] ?? 0) < minVal) return false;
      }
      return true;
    });
  }, [baseFilteredFoods, caloriePreset, macroMin]);

  const hasActiveFilters = caloriePreset !== 0 || Object.values(macroMin)?.some((v) => v !== '');

  const resetFilters = useCallback(() => {
    setCaloriePreset(0);
    setMacroMin({ protein: '', carbs: '', fat: '' });
  }, []);

  const recentFoods = [
    { id: 'fruit-1', name: 'Manzana', calories: 52, defaultServing: 182 },
    { id: 'protein-1', name: 'Pechuga de Pollo', calories: 165, defaultServing: 100 },
    { id: 'grain-2', name: 'Arroz Integral', calories: 111, defaultServing: 195 }
  ];

  const handleFoodSelect = (food) => {
    setSelectedFood(food);
    const defaultG = food?.defaultServing || 100;
    setGrams(defaultG?.toString());
    setActivePreset(1);
    setShowRecent(false);
  };

  const handleServingPreset = (idx) => {
    setActivePreset(idx);
    const defaultG = selectedFood?.defaultServing || 100;
    const newGrams = Math.round(defaultG * SERVING_PRESETS?.[idx]?.multiplier);
    setGrams(newGrams?.toString());
  };

  const handleQuickGram = (g) => {
    setGrams(g?.toString());
    setActivePreset(null);
  };

  const handleGramsChange = (e) => {
    setGrams(e?.target?.value);
    setActivePreset(null);
  };

  const handleBarcodeFound = (product) => {
    setScannedProduct(product);
    setSelectedFood(product);
    setGrams(product?.defaultServing?.toString() || '100');
    setActivePreset(1);
    setShowRecent(false);
    setInputValue('');
    setSearchQuery('');
  };

  const handleBarcodeResult = useCallback((product) => {
    setScannedProduct(product);
    setSelectedFood(product);
    setGrams(product?.defaultServing?.toString() || '100');
    setActivePreset(1);
    setShowRecent(false);
    setInputValue('');
    setSearchQuery('');
  }, []);

  const handleAddFood = () => {
    if (!selectedFood || !grams) return;
    const gramsValue = parseFloat(grams);
    const nutrition = calculateNutrition(selectedFood, gramsValue);
    onAddFood({
      name: selectedFood?.name,
      food_name: selectedFood?.name,
      calories: nutrition?.calories,
      quantity: gramsValue,
      unit: 'g',
      protein: nutrition?.protein,
      carbs: nutrition?.carbs,
      fat: nutrition?.fat,
      fiber: nutrition?.fiber,
      portionMultiplier: gramsValue / 100
    });
    setInputValue('');
    setSearchQuery('');
    setSelectedCategory('Todas');
    setSelectedFood(null);
    setGrams('');
    setShowRecent(true);
    setActivePreset(1);
    resetFilters();
  };

  const handleClose = () => {
    setInputValue('');
    setSearchQuery('');
    setSelectedCategory('Todas');
    setSelectedFood(null);
    setGrams('');
    setShowRecent(true);
    setScannedProduct(null);
    setActivePreset(1);
    resetFilters();
    setShowFilters(false);
    onClose();
  };

  const handleCategorySelect = useCallback((category) => {
    setSelectedCategory(category);
    setShowRecent(false);
  }, []);

  // Prevent horizontal scroll in category bar from bubbling to modal vertical scroll
  const handleCategoryWheel = useCallback((e) => {
    const el = e?.currentTarget;
    const isScrollable = el?.scrollWidth > el?.clientWidth;
    if (isScrollable) {
      e?.stopPropagation();
    }
  }, []);

  if (!isOpen) return null;

  const showResults = !showRecent || searchQuery || inputValue || selectedCategory !== 'Todas';

  // Computed nutrition for selected food + grams
  const gramsValue = parseFloat(grams) || 0;
  const nutrition = selectedFood && gramsValue > 0 ? calculateNutrition(selectedFood, gramsValue) : null;
  const macroCalTotal = nutrition
    ? (nutrition?.protein * 4) + (nutrition?.carbs * 4) + (nutrition?.fat * 9)
    : 0;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={handleClose}
      />

      <div className="relative w-full max-w-3xl bg-card rounded-xl shadow-wellness-lg animate-scale-in elevation-3 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border/40">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Agregar Alimento</h2>
            <p className="text-sm text-muted-foreground capitalize">Agregando a {mealType}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBarcodeScanner(true)}
              className="flex items-center gap-1.5 text-primary border-primary/30 hover:bg-primary/5"
            >
              <Icon name="ScanLine" size={16} />
              <span className="hidden sm:inline">Escanear</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Scanned product banner */}
          {scannedProduct && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-secondary/10 rounded-lg border border-secondary/20">
              <Icon name="ScanLine" size={16} className="text-secondary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">Producto escaneado</p>
                <p className="text-sm font-medium text-foreground truncate">{scannedProduct?.name}</p>
              </div>
              <button
                onClick={() => { setScannedProduct(null); setSelectedFood(null); setGrams(''); }}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={14} />
              </button>
            </div>
          )}

          {/* Search & Filters */}
          <div className="mb-4">
            <div className="flex gap-2 mb-3">
              <input
                type="search"
                placeholder="Buscar alimentos..."
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e?.target?.value);
                  setShowRecent(false);
                }}
                className="flex h-10 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
              <button
                onClick={() => setShowFilters((v) => !v)}
                className={`flex items-center gap-1.5 px-3 h-10 rounded-md border text-sm font-medium transition-colors whitespace-nowrap ${
                  showFilters || hasActiveFilters
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-input bg-background text-muted-foreground hover:bg-muted/40'
                }`}
              >
                <Icon name="SlidersHorizontal" size={15} />
                <span>Filtros</span>
                {hasActiveFilters && (
                  <span className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary-foreground text-primary text-[10px] font-bold">
                    {(caloriePreset !== 0 ? 1 : 0) + Object.values(macroMin)?.filter((v) => v !== '')?.length}
                  </span>
                )}
              </button>
            </div>

            {/* Category Filter */}
            <div
              className="flex gap-2 overflow-x-auto pb-2"
              onWheel={handleCategoryWheel}
            >
              {categories?.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategorySelect(category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedCategory === category
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/30 text-muted-foreground hover:bg-muted/50'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Expanded Filters Panel */}
            {showFilters && (
              <div className="mt-3 p-4 bg-muted/20 rounded-xl border border-border/40 space-y-4">
                {/* Calorie Range */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Icon name="Flame" size={13} className="text-orange-400" />
                    Rango de Calorías (por 100g)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {CALORIE_PRESETS?.map((preset, idx) => (
                      <button
                        key={preset?.label}
                        onClick={() => setCaloriePreset(idx)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          caloriePreset === idx
                            ? 'bg-orange-500 text-white' :'bg-background border border-border/60 text-muted-foreground hover:bg-muted/40'
                        }`}
                      >
                        {preset?.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Macronutrient Minimums */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Icon name="BarChart2" size={13} className="text-primary" />
                    Mínimo de Macronutrientes (g/100g)
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {MACRO_FILTERS?.map(({ key, label, color }) => (
                      <div key={key}>
                        <label className={`block text-xs font-medium mb-1 ${color}`}>{label} ≥</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          placeholder="0"
                          value={macroMin?.[key]}
                          onChange={(e) => setMacroMin((prev) => ({ ...prev, [key]: e?.target?.value }))}
                          className="w-full h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Reset filters */}
                {hasActiveFilters && (
                  <button
                    onClick={resetFilters}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name="RotateCcw" size={12} />
                    Limpiar filtros
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Recent Foods */}
          {showRecent && !searchQuery && !inputValue && selectedCategory === 'Todas' && !hasActiveFilters && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <Icon name="Clock" size={16} className="text-muted-foreground" />
                Alimentos Recientes
              </h3>
              <div className="grid gap-2">
                {recentFoods?.map((food) => (
                  <button
                    key={food?.id}
                    onClick={() => handleFoodSelect(food)}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors text-left"
                  >
                    <div className="flex items-center space-x-3">
                      <Icon name="Apple" size={16} className="text-secondary" />
                      <span className="font-medium text-foreground">{food?.name}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">{food?.calories} cal/100g</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search Results */}
          {(showResults || hasActiveFilters) && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
                  <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-1.5 rounded-full bg-primary/15 text-primary text-xs font-bold">
                    {filteredFoods?.length}
                  </span>
                  {filteredFoods?.length === 1 ? 'resultado' : 'resultados'}
                  {hasActiveFilters && (
                    <span className="text-xs text-muted-foreground font-normal">
                      (filtrado de {baseFilteredFoods?.length})
                    </span>
                  )}
                </h3>
                {hasActiveFilters && filteredFoods?.length === 0 && (
                  <button onClick={resetFilters} className="text-xs text-primary hover:underline">
                    Limpiar filtros
                  </button>
                )}
              </div>

              {filteredFoods?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="w-14 h-14 bg-muted/30 rounded-xl flex items-center justify-center mb-3">
                    <Icon name="SearchX" size={28} className="text-muted-foreground/50" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">No se encontraron alimentos</p>
                  <p className="text-xs text-muted-foreground mt-1 mb-4">
                    {hasActiveFilters
                      ? 'Intenta ajustar o limpiar los filtros activos' :'Prueba con otro término de búsqueda'}
                  </p>
                  <button
                    onClick={() => {
                      resetFilters();
                      setInputValue('');
                      setSearchQuery('');
                      setSelectedCategory('Todas');
                      setShowRecent(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors"
                  >
                    <Icon name="RotateCcw" size={13} />
                    Restablecer búsqueda
                  </button>
                </div>
              ) : (
                <div className="grid gap-2 max-h-[300px] overflow-y-auto">
                  {filteredFoods?.map((food) => (
                    <button
                      key={food?.id}
                      onClick={() => handleFoodSelect(food)}
                      className={`flex items-center justify-between p-3 rounded-lg transition-colors text-left ${
                        selectedFood?.id === food?.id
                          ? 'bg-primary/10 border border-primary/20' :'bg-muted/30 hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon name="Apple" size={16} className="text-secondary" />
                        <div>
                          <p className="font-medium text-foreground">{food?.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {food?.category} • {food?.calories} cal/100g
                          </p>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        <p className="text-sm font-medium text-foreground">{food?.calories}</p>
                        <p className="text-xs text-muted-foreground">cal/100g</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ─── Selected Food: Serving Selector + Nutrition Breakdown ─── */}
          {selectedFood && (
            <div className="bg-primary/5 rounded-xl border border-primary/15 overflow-hidden mb-6">
              {/* Food header */}
              <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-primary/10">
                <div>
                  <h3 className="font-semibold text-foreground text-base leading-tight">{selectedFood?.name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{selectedFood?.category}</p>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <p className="text-2xl font-bold text-primary leading-none">
                    {nutrition?.calories ?? selectedFood?.calories}
                  </p>
                  <p className="text-xs text-muted-foreground">kcal</p>
                </div>
              </div>

              <div className="p-4 space-y-4">
                {/* Serving Preset Buttons */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Icon name="Utensils" size={12} />
                    Tamaño de porción
                    {selectedFood?.servingUnit && (
                      <span className="normal-case font-normal text-muted-foreground/70">
                        — porción base: {selectedFood?.defaultServing}g ({selectedFood?.servingUnit})
                      </span>
                    )}
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {SERVING_PRESETS?.map((preset, idx) => {
                      const presetGrams = Math.round((selectedFood?.defaultServing || 100) * preset?.multiplier);
                      return (
                        <button
                          key={idx}
                          onClick={() => handleServingPreset(idx)}
                          className={`flex flex-col items-center justify-center py-2.5 px-1 rounded-lg border text-center transition-all ${
                            activePreset === idx
                              ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                              : 'bg-background border-border/60 text-muted-foreground hover:bg-muted/40 hover:border-primary/30'
                          }`}
                        >
                          <span className="text-xs font-semibold leading-tight">{preset?.label}</span>
                          <span className={`text-[11px] mt-0.5 ${activePreset === idx ? 'text-primary-foreground/80' : 'text-muted-foreground/70'}`}>
                            {presetGrams}g
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Quick gram buttons + custom input */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Icon name="Scale" size={12} />
                    Cantidad exacta (gramos)
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {QUICK_GRAM_OPTIONS?.map((g) => (
                      <button
                        key={g}
                        onClick={() => handleQuickGram(g)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                          activePreset === null && parseFloat(grams) === g
                            ? 'bg-secondary text-secondary-foreground border-secondary'
                            : 'bg-background border-border/60 text-muted-foreground hover:bg-muted/40'
                        }`}
                      >
                        {g}g
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={grams}
                      onChange={handleGramsChange}
                      min="1"
                      step="1"
                      placeholder="Ej: 100"
                      className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                    <button
                      onClick={() => {
                        setGrams((selectedFood?.defaultServing || 100)?.toString());
                        setActivePreset(1);
                      }}
                      className="px-3 h-9 rounded-md border border-border/60 bg-background text-xs text-muted-foreground hover:bg-muted/40 whitespace-nowrap transition-colors"
                    >
                      Usar sugerida
                    </button>
                  </div>
                </div>

                {/* Nutritional Breakdown */}
                {nutrition && gramsValue > 0 && (
                  <div className="bg-background/60 rounded-lg p-3 border border-border/30">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <Icon name="BarChart3" size={12} className="text-primary" />
                      Desglose nutricional para {gramsValue}g
                    </p>

                    {/* Calorie + macro summary row */}
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      <div className="col-span-1 flex flex-col items-center justify-center bg-primary/10 rounded-lg py-2.5 px-1">
                        <p className="text-[10px] text-muted-foreground leading-none mb-1">Calorías</p>
                        <p className="text-xl font-bold text-primary leading-none">{nutrition?.calories}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">kcal</p>
                      </div>
                      <div className="col-span-3 grid grid-cols-3 gap-2">
                        <div className="flex flex-col items-center justify-center bg-blue-500/10 rounded-lg py-2 px-1">
                          <p className="text-[10px] text-blue-500/80 leading-none mb-1">Proteína</p>
                          <p className="text-base font-bold text-blue-600 dark:text-blue-400 leading-none">{nutrition?.protein}g</p>
                        </div>
                        <div className="flex flex-col items-center justify-center bg-amber-500/10 rounded-lg py-2 px-1">
                          <p className="text-[10px] text-amber-500/80 leading-none mb-1">Carbos</p>
                          <p className="text-base font-bold text-amber-600 dark:text-amber-400 leading-none">{nutrition?.carbs}g</p>
                        </div>
                        <div className="flex flex-col items-center justify-center bg-rose-500/10 rounded-lg py-2 px-1">
                          <p className="text-[10px] text-rose-500/80 leading-none mb-1">Grasa</p>
                          <p className="text-base font-bold text-rose-600 dark:text-rose-400 leading-none">{nutrition?.fat}g</p>
                        </div>
                      </div>
                    </div>

                    {/* Macro percentage bars */}
                    <div className="space-y-2">
                      <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wide font-medium mb-1">% de calorías por macro</p>
                      <MacroBar
                        label="Proteína"
                        value={nutrition?.protein}
                        total={macroCalTotal > 0 ? macroCalTotal / 4 : 1}
                        color="text-blue-500"
                        bgColor="bg-blue-500"
                        textColor="text-blue-500"
                      />
                      <MacroBar
                        label="Carbos"
                        value={nutrition?.carbs}
                        total={macroCalTotal > 0 ? macroCalTotal / 4 : 1}
                        color="text-amber-500"
                        bgColor="bg-amber-500"
                        textColor="text-amber-500"
                      />
                      <MacroBar
                        label="Grasa"
                        value={nutrition?.fat}
                        total={macroCalTotal > 0 ? macroCalTotal / 9 : 1}
                        color="text-rose-500"
                        bgColor="bg-rose-500"
                        textColor="text-rose-500"
                      />
                      {nutrition?.fiber > 0 && (
                        <div className="flex items-center gap-2 pt-1 border-t border-border/20 mt-1">
                          <span className="text-xs font-medium w-16 shrink-0 text-green-500">Fibra</span>
                          <span className="text-xs text-muted-foreground">{nutrition?.fiber}g</span>
                        </div>
                      )}
                    </div>

                    {/* Macro calorie breakdown */}
                    {macroCalTotal > 0 && (
                      <div className="mt-3 pt-2 border-t border-border/20 flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground">Calorías de macros</span>
                        <div className="flex items-center gap-3 text-[10px]">
                          <span className="text-blue-500 font-medium">{Math.round(nutrition?.protein * 4)} kcal prot</span>
                          <span className="text-amber-500 font-medium">{Math.round(nutrition?.carbs * 4)} kcal carb</span>
                          <span className="text-rose-500 font-medium">{Math.round(nutrition?.fat * 9)} kcal gras</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3">
            <Button variant="outline" fullWidth onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              variant="default"
              fullWidth
              onClick={handleAddFood}
              disabled={!selectedFood || !grams || parseFloat(grams) <= 0}
            >
              Agregar Alimento
            </Button>
          </div>
        </div>
      </div>

      <BarcodeScannerModal
        isOpen={showBarcodeScanner}
        onClose={() => setShowBarcodeScanner(false)}
        onFoodFound={handleBarcodeFound}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AddFoodModal;